import { FC, Fragment, useState, useMemo, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation as useTranslationHook } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Link, useNavigate } from 'react-router-dom'
import { Loader } from '../../components/loader'
import { OfferCard } from '../../components/offer-card'
import { RouterPathes } from '../../constants/router-paths'
import { url } from '../../constants/urls'
import { useTranslation } from '../../i18n/useTranslation'
import { Offer } from '../../interface/offerInterface'
import { navBarSelector } from '../../redux/slices/navbarSlice'
import { getOffers } from '../../services/offers/offers'
import { nextOccurrenceFromHHmm } from '../../utils/nextOccurrenceFromHHmm'
import { useGuildOffers, useGuildOverview } from '../../services/guilds/GET'
import { GuildOffer } from '../../interface/guildOffersInterface'
import { GuildsOfferCard } from '../../components/guilds-offer-card/guildsOfferCard'

const DEFAULT_RETRY_COUNT = 2
const DEFAULT_REFETCH_INTERVAL = 15000
const NO_CATEGORY = 'no_category'
const SELECTED_OFFER_TYPE_STORAGE_KEY = 'selectedOfferType'

const REGULAR_CATEGORY_ORDER = [
  'special_offers',
  'seasonal_offers',
  'daily_market',
  'tomes_market',
  'guilds',
  'currencies',
  'power_chests',
  'bounties'
] as const
// todo: unify this later, now Sid want to have this so less testing is needed
const GUILD_CATEGORY_ORDER = [
  'special_offers',
  'seasonal_offers',
  'daily_market',
  'tomes_market',
  'guilds',
  'currencies',
  'power_chests',
  'bounties'
] as const

type OfferType = 'guild' | 'regular'

const setSelectedOfferTypeInStorage = (offerType: OfferType) => {
  localStorage.setItem(SELECTED_OFFER_TYPE_STORAGE_KEY, offerType)
}

type GroupableOffer = {
  category?: string | null
}

function groupOffersByCategory<T extends GroupableOffer>(
  offers: T[]
): Map<string, T[]> {
  return offers.reduce((acc, offer) => {
    const category = offer.category ?? NO_CATEGORY
    if (!acc.has(category)) {
      acc.set(category, [])
    }
    acc.get(category)?.push(offer)
    return acc
  }, new Map<string, T[]>())
}

function getAvailableCategories<T>(
  categoryOrder: readonly string[],
  groupedOffers: Map<string, T[]>
): string[] {
  const categories = categoryOrder.reduce<string[]>((acc, category) => {
    const categoryOffers = groupedOffers.get(category)
    if (categoryOffers?.length) {
      acc.push(category)
    }
    return acc
  }, [])

  const noCategoryOffers = groupedOffers.get(NO_CATEGORY)
  if (noCategoryOffers?.length) {
    categories.push(NO_CATEGORY)
  }

  return categories
}

export const Home: FC = () => {
  const { i18n } = useTranslationHook()
  const { offersTranslations, offerCardTranslations, buttonsTranslations } =
    useTranslation()
  const currentLang = i18n.language

  const navigate = useNavigate()
  const returnLoginUrl = window.location.href
  const redirectLoginUrl = url.redirectLogin(returnLoginUrl)

  const { isLoggedIn } = useSelector(navBarSelector)

  const { data: guildOverview } = useGuildOverview()
  const { data: guildOffers } = useGuildOffers()

  const [selectedOfferType, setSelectedOfferType] = useState<OfferType>(
    () =>
      (localStorage.getItem(SELECTED_OFFER_TYPE_STORAGE_KEY) as OfferType) ??
      'regular'
  )

  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)

  const { data: offersResponse, isLoading } = useQuery({
    queryKey: ['offers'],
    queryFn: getOffers,
    retry: DEFAULT_RETRY_COUNT,
    refetchInterval: DEFAULT_REFETCH_INTERVAL
  })

  const offers = offersResponse?.offers ?? []
  const guildOffersList = guildOffers?.offers ?? []
  const hasGuildOffers = (guildOffers?.offers?.length ?? 0) > 0
  
  useEffect(() => {
    const isGuildOfferAvailable =
      !guildOverview?.isManagementAllowed || !hasGuildOffers
      
    if (isGuildOfferAvailable && selectedOfferType === 'guild') {
      setSelectedOfferType('regular')
      setSelectedOfferTypeInStorage('regular')
      return
    }

    setSelectedOfferTypeInStorage(selectedOfferType)
  }, [guildOverview?.isManagementAllowed, hasGuildOffers, selectedOfferType])

  const onProceedToCheckout = (id: string, type: 'regular' | 'guild') => {
    setSelectedOfferTypeInStorage(type)
    navigate(`${RouterPathes.offerCheckout}?offerId=${id}&type=${type}`)
  }

  const groupedOffers = useMemo(() => groupOffersByCategory(offers), [offers])

  const groupedGuildOffers = useMemo(
    () => groupOffersByCategory(guildOffersList),
    [guildOffersList]
  )

  const availableCategories = useMemo(
    () => getAvailableCategories(REGULAR_CATEGORY_ORDER, groupedOffers),
    [groupedOffers]
  )

  const availableGuildCategories = useMemo(
    () => getAvailableCategories(GUILD_CATEGORY_ORDER, groupedGuildOffers),
    [groupedGuildOffers]
  )

  const scrollToCategory = (category: string, type: 'regular' | 'guild') => {
    setSelectedCategory(category)
    const element = document.getElementById(`${type}-category-${category}`)
    if (!element) {
      return
    }

    element.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  const getCategoryName = (category: string) =>
    category === NO_CATEGORY
      ? i18n.t('offers.noCategory', { defaultValue: 'No Category' })
      : i18n.t(`offers.category.${category}`, { defaultValue: category })

  const getDescription = (
    offer: Offer | GuildOffer,
    type: 'regular' | 'guild'
  ) => {
    const supply =
      type === 'regular'
        ? (offer as Offer).individualSupply
        : (offer as GuildOffer).guildFounderSupply

    const maxSupply =
      type === 'regular'
        ? (offer as Offer).maxIndividualSupply
        : (offer as GuildOffer).maxFounderSupply

    if (supply !== undefined) {
      const isUnlimited =
        maxSupply === null || maxSupply === undefined || maxSupply < 0

      if (isUnlimited) {
        return [
          `${supply}`,
          type === 'regular'
            ? offerCardTranslations.rewardsPerPixionID
            : offerCardTranslations.rewardsPerGuildFounder
        ].join(' ')
      }

      if (maxSupply !== undefined) {
        if (supply === 0) {
          const maxClaimDisplay = `${maxSupply}/${maxSupply}`

          return offer.refreshTime
            ? [maxClaimDisplay, offerCardTranslations.maxClaimRefreshesIn].join(
                ' '
              )
            : [maxClaimDisplay].join(' ')
        }

        return [
          `${supply}/${maxSupply}`,
          type === 'regular'
            ? offerCardTranslations.rewardsPerPixionID
            : offerCardTranslations.rewardsPerGuildFounder
        ].join(' ')
      }
    }

    return undefined
  }

  const renderEmptyState = () => (
    <div className='col-span-full flex flex-col items-center justify-center text-center'>
      <img
        src='/assets/no-offer-box.png'
        alt='no-offer-box'
        className='mb-6 size-[300px] sm:size-[400px]'
      />
      <h2 className='absolute text-2xl font-black text-gradient-gold sm:text-3xl'>
        {offersTranslations.storeIsEmpty}
      </h2>
      <p className='absolute top-[52%] text-lg font-black text-gradient-gold sm:text-xl'>
        {offersTranslations.weWillFillStore}
      </p>
    </div>
  )

  if (isLoading) {
    return (
      <div className='poppins-2xl flex flex-1 items-center justify-center'>
        <Loader
          width={80}
          height={80}
        />
      </div>
    )
  }

  const hasRegularOffers = offers.length > 0
  const hasAnyOffers = hasRegularOffers || hasGuildOffers

  const shouldShowEmptyState =
    (!guildOverview?.isManagementAllowed && !hasRegularOffers) ||
    (guildOverview?.isManagementAllowed && !hasAnyOffers)

  if (shouldShowEmptyState) {
    return (
      <div className='relative flex flex-1 items-center justify-center'>
        <img
          src='/assets/no-offer-box.png'
          alt='no-offer-box'
          className='absolute left-1/2 top-[-16%]  w-full max-w-[492px] -translate-x-1/2'
        />

        <div className='z-10 flex flex-col items-center px-2 text-center'>
          <h1 className='inline-block text-4xl font-black text-gradient-gold'>
            {offersTranslations.storeIsEmpty}
          </h1>
          <p className='inline-block text-2xl font-black text-gradient-gold'>
            {offersTranslations.weWillFillStore}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className='z-10  flex w-full flex-col'>
      {!isLoggedIn && (
        <div className='my-10 flex w-full flex-1 items-center justify-center'>
          <Link
            to={redirectLoginUrl}
            className='yellow-button font-border poppins-2xl max-h-[66px] w-full max-w-[287px] text-[22.5px] uppercase sm:max-h-[84px] sm:max-w-[828px] sm:text-[40px]'
          >
            {offersTranslations.signIn}
          </Link>
        </div>
      )}
      {guildOverview?.isManagementAllowed && (
        <div className='z-10 flex flex-col gap-5 bg-[#101221CC] p-3'>
          <div className='flex flex-row items-center justify-center gap-4 sm:flex-row md:justify-start'>
            <button
              type='button'
              className={`font-poppins text-[17.5px] font-black uppercase tracking-wider transition-all duration-200 md:text-[22.5px] ${
                selectedOfferType === 'guild'
                  ? 'opacity-100'
                  : 'opacity-60 hover:opacity-80'
              }`}
              onClick={() => {
                setSelectedOfferType('guild')
                setSelectedCategory(null)
              }}
            >
              {buttonsTranslations.guildOffers}
            </button>
            <div className='h-4 rounded-md border-r-4 border-r-[#FFFFFF] opacity-20' />
            <button
              type='button'
              className={`font-poppins text-[17.5px] font-black uppercase tracking-wider transition-all duration-200 md:text-[22.5px] ${
                selectedOfferType === 'regular'
                  ? 'opacity-100'
                  : 'opacity-60 hover:opacity-80'
              }`}
              onClick={() => {
                setSelectedOfferType('regular')
                setSelectedCategory(null)
              }}
            >
              {buttonsTranslations.regularOffers}
            </button>
          </div>
        </div>
      )}
      {selectedOfferType === 'regular' &&
        hasRegularOffers &&
        availableCategories.length > 0 && (
          <div className='scrollbar-hide z-10 mb-5 flex items-center gap-4 overflow-x-auto overflow-y-hidden bg-[#10122199] p-3'>
            {availableCategories.map((category, index) => (
              <Fragment key={category}>
                <button
                  type='button'
                  className={`whitespace-nowrap font-poppins text-[15px] font-black uppercase tracking-wider transition-all duration-200 md:text-[17.5px] ${
                    selectedCategory === category
                      ? ' scale-105 opacity-100'
                      : ' opacity-60 hover:opacity-80'
                  }`}
                  onClick={() => scrollToCategory(category, 'regular')}
                >
                  {getCategoryName(category)}
                </button>
                {index !== availableCategories.length - 1 && (
                  <div className='h-4 shrink-0 rounded-md border-r-4 border-r-[#FFFFFF] opacity-20' />
                )}
              </Fragment>
            ))}
          </div>
        )}
      {selectedOfferType === 'guild' &&
        hasGuildOffers &&
        availableGuildCategories.length > 0 && (
          <div className='scrollbar-hide z-10 mb-5 flex items-center gap-4 overflow-x-auto overflow-y-hidden bg-[#10122199] p-3'>
            {availableGuildCategories.map((category, index) => (
              <Fragment key={category}>
                <button
                  type='button'
                  className={`whitespace-nowrap font-poppins text-[15px] font-black uppercase tracking-wider transition-all duration-200 md:text-[17.5px] ${
                    selectedCategory === category
                      ? ' scale-105 opacity-100'
                      : ' opacity-60 hover:opacity-80'
                  }`}
                  onClick={() => scrollToCategory(category, 'guild')}
                >
                  {getCategoryName(category)}
                </button>
                {index !== availableGuildCategories.length - 1 && (
                  <div className='h-4 shrink-0 rounded-md border-r-4 border-r-[#FFFFFF] opacity-20' />
                )}
              </Fragment>
            ))}
          </div>
        )}
      <div className='flex w-full flex-col gap-12'>
        {(() => {
          if (!guildOverview?.isManagementAllowed) {
            if (!hasRegularOffers) {
              return renderEmptyState()
            }

            return availableCategories.map(category => {
              const categoryOffers = groupedOffers.get(category) ?? []
              if (categoryOffers.length === 0) return null

              return (
                <div
                  key={category}
                  id={`regular-category-${category}`}
                  className='flex flex-col gap-6'
                >
                  <div className='grid grid-cols-1 place-items-center gap-10 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'>
                    {categoryOffers.map(offer => (
                      <OfferCard
                        key={offer.id}
                        bonus={
                          offer.bonus
                            ? `${offerCardTranslations.bonus} ${offer.bonus}%`
                            : undefined
                        }
                        offerName={offer.title[currentLang]}
                        description={getDescription(offer, 'regular')}
                        price={offer.price}
                        usdcPrice={offer.usdcPrice}
                        expiredAt={
                          offer.endDate ? new Date(offer.endDate) : undefined
                        }
                        imageUrl={offer.sku.imageUrl}
                        backgroundUrl={offer.sku.backgroundUrl}
                        buttonType={offer.sku.buttonType}
                        isLimitReached={
                          offer.individualSupply === 0 &&
                          (offer.maxGlobalSupply === -1 ||
                            offer.globalSupply > 0)
                        }
                        refreshTime={
                          offer.refreshTime
                            ? nextOccurrenceFromHHmm(offer.refreshTime)
                            : undefined
                        }
                        items={offer.sku.items}
                        displayOfferType={offer.sku.displayOfferType}
                        isSoldOut={offer.globalSupply === 0}
                        proceedToCheckout={() =>
                          onProceedToCheckout(offer.id, 'regular')
                        }
                      />
                    ))}
                  </div>
                </div>
              )
            })
          }
          if (selectedOfferType === 'guild') {
            if (!hasGuildOffers) {
              return renderEmptyState()
            }

            return availableGuildCategories.map(category => {
              const categoryOffers = groupedGuildOffers.get(category) ?? []
              if (categoryOffers.length === 0) return null

              return (
                <div
                  key={category}
                  id={`guild-category-${category}`}
                  className='flex flex-col gap-6'
                >
                  <h2 className='font-border poppins-2xl ml-4 md:ml-6'>
                    {getCategoryName(category)}
                  </h2>
                  <div className='grid grid-cols-1 place-items-center gap-10 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'>
                    {categoryOffers.map(offer => (
                      <GuildsOfferCard
                        key={offer.id}
                        bonus={
                          offer.bonus
                            ? `${offerCardTranslations.bonus} ${offer.bonus}%`
                            : undefined
                        }
                        offerName={offer.title[currentLang]}
                        price={offer.price}
                        usdcPrice={offer.usdcPrice}
                        expiredAt={
                          offer.endDate ? new Date(offer.endDate) : undefined
                        }
                        imageUrl={offer.sku.imageUrl}
                        backgroundUrl={offer.sku.backgroundUrl}
                        buttonType={offer.sku.buttonType}
                        isLimitReached={
                          offer.individualSupply === 0 &&
                          (offer.maxGlobalSupply === -1 ||
                            offer.globalSupply > 0)
                        }
                        refreshTime={
                          offer.refreshTime
                            ? nextOccurrenceFromHHmm(offer.refreshTime)
                            : undefined
                        }
                        items={offer.sku.items}
                        displayOfferType={offer.sku.displayOfferType}
                        isSoldOut={offer.globalSupply === 0}
                        description={getDescription(offer, 'guild')}
                        proceedToCheckout={() =>
                          onProceedToCheckout(offer.id, 'guild')
                        }
                      />
                    ))}
                  </div>
                </div>
              )
            })
          }

          if (!hasRegularOffers) {
            return renderEmptyState()
          }

          return availableCategories.map(category => {
            const categoryOffers = groupedOffers.get(category) ?? []
            if (categoryOffers.length === 0) return null

            return (
              <div
                key={category}
                id={`regular-category-${category}`}
                className='flex flex-col gap-6'
              >
                <h2 className='font-border poppins-2xl ml-4 md:ml-6'>
                  {getCategoryName(category)}
                </h2>
                <div className='grid grid-cols-1 place-items-center gap-10 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'>
                  {categoryOffers.map(offer => (
                    <OfferCard
                      key={offer.id}
                      bonus={
                        offer.bonus
                          ? `${offerCardTranslations.bonus} ${offer.bonus}%`
                          : undefined
                      }
                      offerName={offer.title[currentLang]}
                      description={getDescription(offer, 'regular')}
                      price={offer.price}
                      usdcPrice={offer.usdcPrice}
                      expiredAt={
                        offer.endDate ? new Date(offer.endDate) : undefined
                      }
                      imageUrl={offer.sku.imageUrl}
                      backgroundUrl={offer.sku.backgroundUrl}
                      buttonType={offer.sku.buttonType}
                      isLimitReached={
                        offer.individualSupply === 0 &&
                        (offer.maxGlobalSupply === -1 || offer.globalSupply > 0)
                      }
                      refreshTime={
                        offer.refreshTime
                          ? nextOccurrenceFromHHmm(offer.refreshTime)
                          : undefined
                      }
                      items={offer.sku.items}
                      displayOfferType={offer.sku.displayOfferType}
                      isSoldOut={offer.globalSupply === 0}
                      proceedToCheckout={() =>
                        onProceedToCheckout(offer.id, 'regular')
                      }
                    />
                  ))}
                </div>
              </div>
            )
          })
        })()}
      </div>
    </div>
  )
}
